/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What Cancel puts back, and when there is nothing to put back** (`#1262`).
 *
 * Every mapping editor opens one row at a time and takes a snapshot of it, so Cancel can restore
 * what was there when the editor opened. Five of them do this — the request parameters, the request
 * headers, the ORTB body, the response mapping and the round plan's override editor — and all five
 * also **add a row and open it in the same gesture**.
 *
 * For such a row the snapshot *is* the row that was just created, so restoring it is a no-op and the
 * row survives a Cancel. Todd, user-testing on 2026-09-08: *"adding a field, then canceling out of
 * mapping still leaves the field in place, unset, forcing the user to delete the field."*
 *
 * ## The rule
 *
 * > **Cancel on a row that did not exist when the editor opened removes it.**
 *
 * Adding and opening were **one click**, so Cancel undoes that one act. Leaving the row makes Cancel
 * mean two different things depending on how the row got there, and the operator then needs a second
 * gesture to undo one — which is `#1045` inside a button: the label describes an effect it does not
 * have in that case.
 *
 * It is *an exit is not a decision* running the other way. That rule exempts Cancel from prompting
 * **because it is labelled with its outcome**; the exemption is only earned while the outcome the
 * label promises is the one the operator gets.
 *
 * ## The counter-case, and why it lost (Todd, 2026-09-11)
 *
 * `OrtbBodyEditor` carried the opposite decision in a comment — *"removing the field is the ✕
 * action, not an edit-discard"* — and it names something real: add a field, edit it, and you might
 * want only the **edit** discarded, keeping the field with the default mapping the catalog gave it.
 *
 * Ruled against, and the reason is worth keeping: **on a row you have just created there is nothing
 * behind the edit except that default**, which is one Apply away and is what the operator reaches by
 * applying and re-opening. Against that, the other reading costs a second gesture on the commonest
 * act of all — changing your mind about a field you just added.
 *
 * *This was the only surface that had decided, which is why it was asked rather than assumed:* the
 * issue reported four editors that "all agree", and it is five, one of them disagreeing in writing.
 *
 * ## Why it is here rather than five times
 *
 * The five editors key their rows differently — by `id`, by index, by a path label — so what they
 * share is not the lookup but **the rule**, and a rule written as a condition in more than one place
 * is what `#1269` is about. {@link RowEditSnapshot} makes every site that opens an editor answer
 * where its row came from, and {@link rowsAfterCancel} is the only place that decides what to do
 * with the answer.
 */

import { confirmedOrigin, type ValueOrigin } from '@value-provenance';

/** Where the row being edited came from. Named rather than a boolean, so a call site reads. */
export type RowOrigin =
  /** It was already in the list when the editor opened. Cancel puts it back. */
  | 'existing'
  /** The gesture that opened the editor created it. Cancel removes it. */
  | 'created';

/**
 * The row an editor opened on, and where it came from.
 *
 * `origin` is **required**, deliberately: the defect this closes is five editors that never asked
 * the question, so a snapshot that can be built without answering it would let the sixth editor
 * reintroduce the defect silently — *an optional prop through a shared component renders nothing*,
 * arriving at a value instead of at a component.
 */
export interface RowEditSnapshot<Row> {
  readonly row: Row;
  readonly origin: RowOrigin;
}

/**
 * The list after Cancel: the snapshot restored, or the row gone where it was never there.
 *
 * `isEdited` rather than a key accessor, because the five editors identify the open row by three
 * different things and the index is the only one all of them can produce. A `null` snapshot is
 * *nothing was open*, which changes nothing.
 */
export function rowsAfterCancel<Row>(
  rows: readonly Row[],
  snapshot: RowEditSnapshot<Row> | null,
  isEdited: (row: Row, index: number) => boolean,
): Row[] {
  if (!snapshot) return [...rows];
  return snapshot.origin === 'existing'
    ? rows.map((row, index) => (isEdited(row, index) ? snapshot.row : row))
    : rows.filter((row, index) => !isEdited(row, index));
}

/**
 * **The list after Apply: what the operator accepted, and what they changed** — two rules about
 * provenance, one for every editor.
 *
 * - **A detected guess becomes a decision** (`#1001`, Todd 2026-09-15). Opening a row and pressing
 *   Apply is the operator looking at the mapping and accepting it, so its confidence mark and its
 *   revert target clear (`confirmedOrigin`).
 * - **A row that came from a template and was changed is overridden here** (Todd, 2026-09-24). A
 *   copy keeps no link to its template, and the chip went on saying `from template` over a value the
 *   operator had just replaced: *"the user did override something that came from the parent even
 *   though it's a copy, and they may not remember what the template used to be."* Changed means the
 *   applied row differs from the one the editor opened on, apart from its row id and provenance, so
 *   opening a row and applying it untouched keeps `from template`.
 *
 * Here, beside {@link rowsAfterCancel}, because Apply and Cancel are the two things an editor does to
 * the row it opened, and a rule written once per surface is how the sixth editor leaves it out.
 * `snapshot` is **required and nullable** for the same reason `RowEditSnapshot.origin` is: an editor
 * that could skip it would skip the second rule silently. A row the gesture created is not a template
 * row being changed, whatever it carries.
 *
 * Returns **the same array** when nothing changed, so an Apply that changes no provenance does not
 * emit a change — a list that is `!==` but equal reads as an edit to every caller that compares.
 */
export function rowsAfterApply<Row extends { provenance?: ValueOrigin }>(
  rows: Row[],
  isEdited: (row: Row, index: number) => boolean,
  snapshot: RowEditSnapshot<Row> | null,
): Row[] {
  let changed = false;
  const next = rows.map((row, index) => {
    if (!isEdited(row, index)) return row;
    let provenance = confirmedOrigin(row.provenance);
    if (
      provenance?.kind === 'template' &&
      snapshot?.origin === 'existing' &&
      rowContent(snapshot.row) !== rowContent(row)
    ) {
      provenance = { kind: 'overridden' };
    }
    if (provenance === row.provenance) return row;
    changed = true;
    return { ...row, provenance };
  });
  return changed ? next : rows;
}

/** What a row says, without the id a projection mints and the provenance being decided. */
function rowContent(row: unknown): string {
  if (row === null || typeof row !== 'object') return JSON.stringify(row);
  const { id: _id, provenance: _provenance, ...rest } = row as Record<string, unknown>;
  return JSON.stringify(rest);
}
